import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  Download,
  Trash2,
  FileText,
  FileImage,
  FileType,
  File as FileIcon,
  FolderOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import PropertyFilesDialog from '@/components/dialogs/PropertyFilesDialog';
import {
  listAllOrgFiles,
  getProperties,
  deletePropertyFile,
  downloadPropertyFileUrl,
} from '@/lib/api';
import {
  CATEGORY_LABELS,
} from '@/components/PropertyFilesPanel';
import { useAuth } from '@/contexts/AuthContext';

const CATEGORY_TONE = {
  house_info: 'bg-blue-100 text-blue-800',
  house_rules: 'bg-rose-100 text-rose-800',
  wifi_access: 'bg-sky-100 text-sky-800',
  cleaning_guide: 'bg-emerald-100 text-emerald-800',
  maintenance_guide: 'bg-amber-100 text-amber-800',
  emergency_contacts: 'bg-red-100 text-red-800',
  other: 'bg-slate-100 text-slate-800',
};

const iconForType = (contentType) => {
  if (!contentType) return <FileIcon className="h-5 w-5" />;
  if (contentType.startsWith('image/')) return <FileImage className="h-5 w-5" />;
  if (contentType.includes('pdf')) return <FileType className="h-5 w-5" />;
  if (contentType.includes('word')) return <FileText className="h-5 w-5" />;
  if (contentType === 'text/plain') return <FileText className="h-5 w-5" />;
  return <FileIcon className="h-5 w-5" />;
};

const formatBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const FilesView = () => {
  const { user } = useAuth();
  const canDelete = ['property_manager', 'owner'].includes(user?.role);

  const [files, setFiles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [dialogProperty, setDialogProperty] = useState(null);

  const refresh = async () => {
    try {
      setLoading(true);
      const [filesRes, propsRes] = await Promise.all([
        listAllOrgFiles(),
        getProperties(),
      ]);
      setFiles(filesRes.data || []);
      setProperties(propsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const propertyMap = useMemo(() => {
    const m = {};
    properties.forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [properties]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return files.filter((f) => {
      if (categoryFilter !== 'all' && f.category !== categoryFilter) return false;
      if (propertyFilter !== 'all' && f.property_id !== propertyFilter) return false;
      if (!q) return true;
      const prop = propertyMap[f.property_id]?.name || '';
      return (
        f.filename.toLowerCase().includes(q) ||
        (f.description || '').toLowerCase().includes(q) ||
        (f.uploaded_by_name || '').toLowerCase().includes(q) ||
        prop.toLowerCase().includes(q)
      );
    });
  }, [files, query, categoryFilter, propertyFilter, propertyMap]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ${row.filename}? This cannot be undone.`)) return;
    try {
      await deletePropertyFile(row.property_id, row.id);
      toast.success('File deleted');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="files-view">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Files</h1>
        <p className="text-slate-500 mt-1">
          Every doc, guide, and image attached to your properties — searchable
          in one place.
        </p>
      </div>

      <Card className="p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search filename, description, uploader, property…"
              className="pl-9"
              data-testid="files-search-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="files-category-filter">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                <SelectItem key={val} value={val}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={propertyFilter} onValueChange={setPropertyFilter}>
            <SelectTrigger data-testid="files-property-filter">
              <SelectValue placeholder="All properties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All properties</SelectItem>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading files…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <FolderOpen className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">
              {files.length === 0
                ? 'No files uploaded yet. Add house info, rules, or job guides to any property.'
                : 'No files match your filters.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {filtered.map((f) => {
              const prop = propertyMap[f.property_id];
              return (
                <li
                  key={f.id}
                  className="flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`files-row-${f.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                    {iconForType(f.content_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {f.filename}
                      </p>
                      <Badge
                        className={`${CATEGORY_TONE[f.category] || CATEGORY_TONE.other} border-transparent text-xs`}
                      >
                        {CATEGORY_LABELS[f.category] || 'Other'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                      {prop && (
                        <button
                          type="button"
                          onClick={() => setDialogProperty(prop)}
                          className="underline decoration-dotted hover:text-slate-800"
                          data-testid={`files-property-link-${prop.id}`}
                        >
                          {prop.name}
                        </button>
                      )}
                      <span>· {formatBytes(f.size_bytes)}</span>
                      <span>· {f.uploaded_by_name || 'Someone'}</span>
                      <span>
                        ·{' '}
                        {(() => {
                          try {
                            return format(parseISO(f.created_at), 'PP');
                          } catch {
                            return '';
                          }
                        })()}
                      </span>
                    </div>
                    {f.description && (
                      <p className="text-xs text-slate-600 mt-1 italic truncate">
                        {f.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <a
                      href={downloadPropertyFileUrl(f.property_id, f.id)}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      title="Download"
                      data-testid={`files-download-${f.id}`}
                    >
                      <Download className="h-4 w-4" />
                    </a>
                    {prop && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDialogProperty(prop)}
                        data-testid={`files-open-property-${f.id}`}
                      >
                        Manage
                      </Button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(f)}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-red-600 hover:bg-red-50"
                        title="Delete"
                        data-testid={`files-delete-${f.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <PropertyFilesDialog
        open={!!dialogProperty}
        onOpenChange={(o) => {
          if (!o) {
            setDialogProperty(null);
            refresh();
          }
        }}
        property={dialogProperty}
      />
    </div>
  );
};

export default FilesView;
